// Engine synth presets. All knobs in one place so the tuner can save/load/export
// JSON blobs that drop straight into source as the shipped default.

export interface PodConfig {
  // Physical parameters of one pod.
  cylinders: number;        // 1–8, shapes uneven-firing pattern
  unevenFiring: number;     // 0–0.5, how irregular cylinder amps are
  intakeCm: number;         // intake pipe length (cm)
  exhaustCm: number;        // exhaust pipe length (cm)
  feedback: number;         // 0–0.99, ringing tail of waveguides
  damping: number;          // 0–1, warmth vs harshness
  pulseWidth: number;       // 0.02–0.6, fraction of firing period the pulse occupies
  pulseGrit: number;        // 0–1, amount of noise inside each pulse body
  firingJitter: number;     // 0–0.4, per-firing amplitude randomness
  detuneCents: number;      // -200..200, cents offset from fundamental
  wobbleDepth: number;      // 0–0.25, slow random walk on pipe length
  wobbleRateHz: number;     // 0–12, LFO rate of the wobble
  // Sub-bass drone: continuous low oscillator at fundamental × droneRatio.
  // The "V-12 underneath" — gives the engine a body the cylinder pulses can't.
  droneRatio: number;       // 1–16, integer-ish multiple of the firing rate
  droneCutoff: number;      // 60–1500 Hz, lowpass on the drone saw
  // High whine: emerges past whineThreshold, sits in the 2–5 kHz scream band.
  whineRatio: number;       // 2–16, multiple of the firing rate
  whineThreshold: number;   // 0–1, throttle level where whine fades in
  // Turbine roar: bandpass noise that sweeps brighter under throttle. The
  // "whoosh power on" lead voice when the synth is doing jet character.
  roarBaseHz: number;       // 100–4000, bandpass center at idle
  roarSweepHz: number;      // 0–6000, added to base at full throttle
  roarBandwidth: number;    // 0.3–4 octaves wide
  roarFloor: number;        // 0–1, how loud the roar is at idle relative to WOT
  roarCurve: number;        // 0.5–3, exponent on throttle for roar growth (>1 = back-loaded)
  // pulseFade: how much the cylinder + exhaust mechanical layers fade out
  // under throttle. 0 = no fade. 1 = silent at WOT. Lets the thump own idle
  // while the roar takes over at speed.
  pulseFade: number;        // 0–1
  // Podrace source-model layers: these are procedural stand-ins for the
  // real-world recordings Burtt layered, not sample playback.
  racecarRatio?: number;       // 1–24, harmonic growl multiple
  racecarFormantHz?: number;   // 200–4500, resonant racecar throat
  racecarGrit?: number;        // 0–1, noise folded into the growl
  propRate?: number;           // 0.5–12, blade/chopper multiple of rpm
  propDepth?: number;          // 0–1, amplitude chop depth
  motorBuzzRatio?: number;     // 8–80, small motor/toothbrush buzz multiple
  motorBuzzDetune?: number;    // 0–1, unstable small-motor pitch drift
  heartbeatDepth?: number;     // 0–1, low pulsing engine amplitude
  heartbeatRateHz?: number;    // 0.5–6, pulse rate at idle
  combDelayMs?: number;        // 1–18, tuned nasal/F1 comb resonance delay
  combFeedback?: number;       // 0–0.97, resonance strength
  combDamping?: number;        // 0–1, darkens the comb feedback path
  combDrive?: number;          // 0–1, nonlinear bite into the comb exciter
}

export interface MixConfig {
  cylinder: number;         // 0–2 — the dry pulse layer
  intake: number;           // 0–2 — intake waveguide
  exhaust: number;          // 0–2 — exhaust waveguide (the thump)
  block: number;            // 0–2 — block vibration noise
  slip: number;             // 0–2 — slip rasp gain
  drone: number;            // 0–2 — sub-bass drone
  whine: number;            // 0–2 — high-RPM scream overtone
  hiss: number;             // 0–2 — release hiss (gated by physics)
  roar: number;             // 0–2 — turbine roar (bandpass noise)
  racecar?: number;         // 0–2 — high-speed racecar growl
  prop?: number;            // 0–2 — warbird/helicopter blade chop
  motor?: number;           // 0–2 — electric shaver/toothbrush buzz
  shift?: number;           // 0–2 — transient gear/cut bark
  comb?: number;            // 0–2 — tuned nasal/F1 pod resonance
  master: number;           // 0–2 — overall pod gain
}

export interface ThrottleResponse {
  // Maps physics state → synth params. Curves are exponentials applied to inputs.
  rpmIdle: number;          // Hz at throttle=0, speed=0
  rpmRedline: number;       // Hz at throttle=1, speed=1
  speedWeight: number;      // 0–1, how much speed (vs throttle) drives RPM
  rpmCurve: number;         // 1=linear, >1=exponential, <1=log-ish
}

export interface TwinPodConfig {
  beatDetuneHz: number;     // L vs R fundamental offset — THE THUMP KNOB
  stereoWidth: number;      // 0–1, pan amount
  podSync: number;          // 0–1, how locked the two pods are (0 = drift freely)
}

export interface AudioPreset {
  name: string;
  pod: PodConfig;
  mix: MixConfig;
  twin: TwinPodConfig;
  throttle: ThrottleResponse;
}

export const AUDIO_PRESET_VERSION = '0.1.0';

// Default = jet/turbine character. The lead voice is a continuous bandpass
// noise roar that sweeps brighter with throttle (the whoosh-power-on). The
// cylinder pulses sit much further back as a "felt" thud underneath. Pitch
// fundamental moves only ~1.5 octaves — most of the perceived crescendo
// comes from the roar's filter sweep and the whine fading in, not from
// pitch climb. Compressor whine has a low threshold so it's faintly there
// at idle.
export const DEFAULT_PRESET: AudioPreset = {
  name: 'turbine',
  pod: {
    cylinders: 2,
    unevenFiring: 0.32,
    intakeCm: 115,
    exhaustCm: 250,
    feedback: 0.94,
    damping: 0.5,
    pulseWidth: 0.38,         // fatter body per thud — fills the slow idle gaps
    pulseGrit: 0.28,
    firingJitter: 0.06,
    detuneCents: 0,
    wobbleDepth: 0.05,
    wobbleRateHz: 0.9,
    droneRatio: 5,
    droneCutoff: 240,
    whineRatio: 5.5,
    whineThreshold: 0.05,
    roarBaseHz: 700,
    roarSweepHz: 2400,
    roarBandwidth: 1.6,
    roarFloor: 0.12,          // less whoosh at idle
    roarCurve: 2.0,           // back-loaded — roar lives at WOT
    pulseFade: 0.25,          // ★ only 25% fade so cylinder + exhaust survive at WOT
    racecarRatio: 3.6,
    racecarFormantHz: 760,
    racecarGrit: 0.42,
    propRate: 2.6,
    propDepth: 0.45,
    motorBuzzRatio: 24,
    motorBuzzDetune: 0.18,
    heartbeatDepth: 0.12,
    heartbeatRateHz: 1.45,
    combDelayMs: 5.9,
    combFeedback: 0.48,
    combDamping: 0.46,
    combDrive: 0.32,
  },
  mix: {
    cylinder: 0.75,           // thump leads at idle
    intake: 0.22,
    exhaust: 1.3,             // heavy exhaust ringing tracks pitch at WOT
    block: 0.32,
    slip: 0,
    drone: 0.8,               // ★ louder — drone is the main RPM cue at speed
    whine: 0.75,
    hiss: 0.5,
    roar: 0.95,               // ★ pulled back so it doesn't wash out the rest
    racecar: 0.42,
    prop: 0.22,
    motor: 0.02,
    shift: 0.18,
    comb: 0.36,
    master: 0.62,
  },
  twin: {
    beatDetuneHz: 0.4,         // slower throb to match slower idle
    stereoWidth: 0.48,
    podSync: 0.25,
  },
  throttle: {
    rpmIdle: 5.5,              // ★ slow individual thuds (was 7)
    rpmRedline: 32,
    speedWeight: 0.5,
    rpmCurve: 1.6,
  },
};

export const PRESETS: Record<string, AudioPreset> = {
  'turbine': DEFAULT_PRESET,

  // Variant: brighter, faster spool. Roar sweeps further, whine punchier.
  'turbine-hot': {
    name: 'turbine-hot',
    pod: {
      cylinders: 2,
      unevenFiring: 0.18,
      intakeCm: 70,
      exhaustCm: 160,
      feedback: 0.85,
      damping: 0.4,
      pulseWidth: 0.22,
      pulseGrit: 0.3,
      firingJitter: 0.05,
      detuneCents: 0,
      wobbleDepth: 0.03,
      wobbleRateHz: 1.5,
      droneRatio: 5,
      droneCutoff: 320,
      whineRatio: 6,
      whineThreshold: 0.0,
      roarBaseHz: 900,
      roarSweepHz: 3200,
      roarBandwidth: 1.3,
      roarFloor: 0.25,
      roarCurve: 1.5,
      pulseFade: 0.7,
    },
    mix: {
      cylinder: 0.4,
      intake: 0.25,
      exhaust: 0.85,
      block: 0.18,
      slip: 0.4,
      drone: 0.4,
      whine: 0.65,
      hiss: 0.4,
      roar: 1.2,
      master: 0.65,
    },
    twin: {
      beatDetuneHz: 1.0,
      stereoWidth: 0.7,
      podSync: 0.4,
    },
    throttle: {
      rpmIdle: 12,
      rpmRedline: 45,
      speedWeight: 0.6,
      rpmCurve: 1.4,
    },
  },

  // The earlier piston-engine character — kept for reference/comparison.
  'whomp-idle': {
    name: 'whomp-idle',
    pod: {
      cylinders: 2,
      unevenFiring: 0.28,
      intakeCm: 110,
      exhaustCm: 235,
      feedback: 0.93,
      damping: 0.5,
      pulseWidth: 0.32,
      pulseGrit: 0.3,
      firingJitter: 0.08,
      detuneCents: 0,
      wobbleDepth: 0.05,
      wobbleRateHz: 1.1,
      droneRatio: 6,
      droneCutoff: 220,
      whineRatio: 6.5,
      whineThreshold: 0.4,
      roarBaseHz: 600,
      roarSweepHz: 1200,
      roarBandwidth: 2,
      roarFloor: 0.2,
      roarCurve: 1.5,
      pulseFade: 0.4,
    },
    mix: {
      cylinder: 0.4,
      intake: 0.3,
      exhaust: 1.15,
      block: 0.35,
      slip: 0.45,
      drone: 0.55,
      whine: 0.35,
      hiss: 0.4,
      roar: 0.25,
      master: 0.7,
    },
    twin: {
      beatDetuneHz: 0.6,
      stereoWidth: 0.6,
      podSync: 0.2,
    },
    throttle: {
      rpmIdle: 10,
      rpmRedline: 60,
      speedWeight: 0.5,
      rpmCurve: 1.7,
    },
  },

  // Off-throttle voice — heavy on hiss, low roar, soft body.
  'coast': {
    name: 'coast',
    pod: {
      cylinders: 2,
      unevenFiring: 0.1,
      intakeCm: 95,
      exhaustCm: 175,
      feedback: 0.82,
      damping: 0.65,
      pulseWidth: 0.3,
      pulseGrit: 0.25,
      firingJitter: 0.1,
      detuneCents: 0,
      wobbleDepth: 0.05,
      wobbleRateHz: 0.9,
      droneRatio: 6,
      droneCutoff: 180,
      whineRatio: 6,
      whineThreshold: 0.0,
      roarBaseHz: 800,
      roarSweepHz: 1500,
      roarBandwidth: 1.8,
      roarFloor: 0.5,
      roarCurve: 1.4,
      pulseFade: 0.5,
    },
    mix: {
      cylinder: 0.18,
      intake: 0.45,
      exhaust: 0.4,
      block: 0.12,
      slip: 0.2,
      drone: 0.4,
      whine: 0.25,
      hiss: 0.7,
      roar: 0.7,
      master: 0.5,
    },
    twin: {
      beatDetuneHz: 0.8,
      stereoWidth: 0.5,
      podSync: 0.2,
    },
    throttle: {
      rpmIdle: 9,
      rpmRedline: 28,
      speedWeight: 0.8,
      rpmCurve: 1.5,
    },
  },

  // Stressed engine — uneven firing, heavier bass body, rougher roar.
  'stress-load': {
    name: 'stress-load',
    pod: {
      cylinders: 3,
      unevenFiring: 0.36,
      intakeCm: 85,
      exhaustCm: 245,
      feedback: 0.94,
      damping: 0.4,
      pulseWidth: 0.24,
      pulseGrit: 0.5,
      firingJitter: 0.18,
      detuneCents: 0,
      wobbleDepth: 0.09,
      wobbleRateHz: 3.2,
      droneRatio: 5,
      droneCutoff: 240,
      whineRatio: 5,
      whineThreshold: 0.05,
      roarBaseHz: 500,
      roarSweepHz: 1800,
      roarBandwidth: 2.2,
      roarFloor: 0.4,
      roarCurve: 1.6,
      pulseFade: 0.55,
    },
    mix: {
      cylinder: 0.32,
      intake: 0.3,
      exhaust: 0.85,
      block: 0.4,
      slip: 0.6,
      drone: 0.6,
      whine: 0.35,
      hiss: 0.5,
      roar: 0.85,
      master: 0.7,
    },
    twin: {
      beatDetuneHz: 1.6,
      stereoWidth: 0.65,
      podSync: 0.15,
    },
    throttle: {
      rpmIdle: 11,
      rpmRedline: 42,
      speedWeight: 0.45,
      rpmCurve: 1.5,
    },
  },
};

export function clonePreset(p: AudioPreset): AudioPreset {
  return {
    name: p.name,
    pod: {
      ...p.pod,
      racecarRatio: p.pod.racecarRatio ?? DEFAULT_PRESET.pod.racecarRatio,
      racecarFormantHz: p.pod.racecarFormantHz ?? DEFAULT_PRESET.pod.racecarFormantHz,
      racecarGrit: p.pod.racecarGrit ?? DEFAULT_PRESET.pod.racecarGrit,
      propRate: p.pod.propRate ?? DEFAULT_PRESET.pod.propRate,
      propDepth: p.pod.propDepth ?? DEFAULT_PRESET.pod.propDepth,
      motorBuzzRatio: p.pod.motorBuzzRatio ?? DEFAULT_PRESET.pod.motorBuzzRatio,
      motorBuzzDetune: p.pod.motorBuzzDetune ?? DEFAULT_PRESET.pod.motorBuzzDetune,
      heartbeatDepth: p.pod.heartbeatDepth ?? DEFAULT_PRESET.pod.heartbeatDepth,
      heartbeatRateHz: p.pod.heartbeatRateHz ?? DEFAULT_PRESET.pod.heartbeatRateHz,
      combDelayMs: p.pod.combDelayMs ?? DEFAULT_PRESET.pod.combDelayMs,
      combFeedback: p.pod.combFeedback ?? DEFAULT_PRESET.pod.combFeedback,
      combDamping: p.pod.combDamping ?? DEFAULT_PRESET.pod.combDamping,
      combDrive: p.pod.combDrive ?? DEFAULT_PRESET.pod.combDrive,
    },
    mix: {
      ...p.mix,
      racecar: p.mix.racecar ?? DEFAULT_PRESET.mix.racecar,
      prop: p.mix.prop ?? DEFAULT_PRESET.mix.prop,
      motor: p.mix.motor ?? DEFAULT_PRESET.mix.motor,
      shift: p.mix.shift ?? DEFAULT_PRESET.mix.shift,
      comb: p.mix.comb ?? DEFAULT_PRESET.mix.comb,
    },
    twin: { ...p.twin },
    throttle: { ...p.throttle },
  };
}
